import type {
  ApiToken,
  Dashboard,
  Datasource,
  Endpoint,
  FlowGraph,
  Json,
  LiveTable,
  SchemaOp,
  ProjectMember,
  ProjectRole,
  Project,
  RunLog,
  StepTrace,
  TokenScope,
  Widget,
} from '@forge/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './api';

export type ProjectWithRole = Project & { role: ProjectRole };

export const qk = {
  projects: ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  overview: (id: string) => ['project', id, 'overview'] as const,
  endpoints: (id: string) => ['project', id, 'endpoints'] as const,
  endpoint: (id: string, endpointId: string) => ['project', id, 'endpoint', endpointId] as const,
  endpointShape: (id: string, endpointId: string) => ['project', id, 'endpoint', endpointId, 'shape'] as const,
  dashboards: (id: string) => ['project', id, 'dashboards'] as const,
  dashboard: (id: string, dashboardId: string) => ['project', id, 'dashboard', dashboardId] as const,
  datasources: (id: string) => ['project', id, 'datasources'] as const,
  datasourceTypes: ['datasource-types'] as const,
  datasourceSchema: (id: string, dsId: string) => ['project', id, 'datasource', dsId, 'schema'] as const,
  datasourceStructure: (id: string, dsId: string) => ['project', id, 'datasource', dsId, 'structure'] as const,
  members: (id: string) => ['project', id, 'members'] as const,
  tokens: (id: string) => ['project', id, 'tokens'] as const,
  logs: (id: string, filters?: unknown) => ['project', id, 'logs', filters ?? {}] as const,
  stats: (id: string, hours: number) => ['project', id, 'stats', hours] as const,
  instance: ['admin', 'instance'] as const,
  adminTokens: ['admin', 'tokens'] as const,
  adminUsers: (search?: string) => ['admin', 'users', search ?? ''] as const,
  mcpTools: ['admin', 'mcp', 'tools'] as const,
};

/* -------------------------------- projects ------------------------------- */

export function useProjects() {
  return useQuery({
    queryKey: qk.projects,
    queryFn: () => api.get<{ projects: ProjectWithRole[] }>('/projects').then((r) => r.projects),
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: qk.project(projectId ?? ''),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ project: ProjectWithRole }>(`/projects/${projectId}`).then((r) => r.project),
  });
}

export interface ProjectOverview {
  project: ProjectWithRole;
  /** `members` is omitted for roles that cannot read the member list. */
  counts: { endpoints: number; published: number; dashboards: number; members?: number };
  /** Omitted for roles that cannot read run logs. */
  stats?: { total: number; success: number; error: number; timeout: number; avgDurationMs: number; p95DurationMs: number };
  endpoints: { id: string; name: string; method: string; path: string; status: string; updatedAt: string }[];
  dashboards: { id: string; name: string; widgets: number; pinned: boolean }[];
  /** Full definitions of the pinned dashboards, so the overview can render them. */
  pinned: Dashboard[];
}

export function useOverview(projectId: string | undefined) {
  return useQuery({
    queryKey: qk.overview(projectId ?? ''),
    enabled: Boolean(projectId),
    queryFn: () => api.get<ProjectOverview>(`/projects/${projectId}/overview`),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug?: string; description?: string }) =>
      api.post<{ project: ProjectWithRole }>('/projects', input).then((r) => r.project),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.projects }),
  });
}

export function useUpdateProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; description?: string }) =>
      api.patch<{ project: ProjectWithRole }>(`/projects/${projectId}`, input).then((r) => r.project),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.projects });
      void qc.invalidateQueries({ queryKey: qk.project(projectId) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.delete(`/projects/${projectId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.projects }),
  });
}

/* -------------------------------- endpoints ------------------------------ */

export function useEndpoints(projectId: string | undefined) {
  return useQuery({
    queryKey: qk.endpoints(projectId ?? ''),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ endpoints: Endpoint[] }>(`/projects/${projectId}/endpoints`).then((r) => r.endpoints),
  });
}

export function useEndpoint(projectId: string | undefined, endpointId: string | undefined) {
  return useQuery({
    queryKey: qk.endpoint(projectId ?? '', endpointId ?? ''),
    enabled: Boolean(projectId && endpointId),
    queryFn: () =>
      api
        .get<{ endpoint: Endpoint }>(`/projects/${projectId}/endpoints/${endpointId}`)
        .then((r) => r.endpoint),
  });
}

export function useCreateEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; method?: string; path?: string }) =>
      api.post<{ endpoint: Endpoint }>(`/projects/${projectId}/endpoints`, input).then((r) => r.endpoint),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.endpoints(projectId) }),
  });
}

export interface SaveGraphInput {
  endpointId: string;
  graph?: FlowGraph;
  name?: string;
  description?: string;
  baseVersion?: number;
  socketId?: string;
}

export function useSaveEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ endpointId, socketId, ...body }: SaveGraphInput) =>
      api
        .patch<{ endpoint: Endpoint }>(`/projects/${projectId}/endpoints/${endpointId}`, body, { socketId })
        .then((r) => r.endpoint),
    onSuccess: (endpoint) => {
      qc.setQueryData(qk.endpoint(projectId, endpoint.id), endpoint);
      void qc.invalidateQueries({ queryKey: qk.endpoints(projectId) });
    },
  });
}

export function usePublishEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ endpointId, publish }: { endpointId: string; publish: boolean }) =>
      api
        .post<{ endpoint: Endpoint }>(
          `/projects/${projectId}/endpoints/${endpointId}/${publish ? 'publish' : 'unpublish'}`,
        )
        .then((r) => r.endpoint),
    onSuccess: (endpoint) => {
      qc.setQueryData(qk.endpoint(projectId, endpoint.id), endpoint);
      void qc.invalidateQueries({ queryKey: qk.endpoints(projectId) });
      void qc.invalidateQueries({ queryKey: qk.overview(projectId) });
    },
  });
}

export function useDeleteEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endpointId: string) => api.delete(`/projects/${projectId}/endpoints/${endpointId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.endpoints(projectId) }),
  });
}

export function useDuplicateEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endpointId: string) =>
      api
        .post<{ endpoint: Endpoint }>(`/projects/${projectId}/endpoints/${endpointId}/duplicate`, {})
        .then((r) => r.endpoint),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.endpoints(projectId) }),
  });
}

export interface RunResponse {
  runId: string;
  status: 'success' | 'error' | 'timeout';
  httpStatus: number;
  headers: Record<string, string>;
  body: Json;
  durationMs: number;
  trace: StepTrace[];
  logs: { level: string; message: string; at: string }[];
  error?: string;
}

export function useRunEndpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      endpointId,
      ...body
    }: {
      endpointId: string;
      params?: Record<string, string>;
      query?: Record<string, string>;
      headers?: Record<string, string>;
      body?: unknown;
      draft?: boolean;
    }) => api.post<RunResponse>(`/projects/${projectId}/endpoints/${endpointId}/run`, body),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: qk.endpointShape(projectId, variables.endpointId) });
      void qc.invalidateQueries({ queryKey: qk.logs(projectId) });
    },
  });
}

export interface EndpointShape {
  hasSample: boolean;
  shape: {
    rowsPath: string;
    columns: { key: string; type: string; distinct: number; categorical: boolean; temporal: boolean }[];
    scalars: { key: string; value: Json }[];
    rowCount: number;
    preview: Record<string, Json>[];
  } | null;
}

export function useEndpointShape(projectId: string | undefined, endpointId: string | undefined) {
  return useQuery({
    queryKey: qk.endpointShape(projectId ?? '', endpointId ?? ''),
    enabled: Boolean(projectId && endpointId),
    queryFn: () => api.get<EndpointShape>(`/projects/${projectId}/endpoints/${endpointId}/shape`),
  });
}

/* ------------------------------- dashboards ------------------------------ */

export function useDashboards(projectId: string | undefined) {
  return useQuery({
    queryKey: qk.dashboards(projectId ?? ''),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ dashboards: Dashboard[] }>(`/projects/${projectId}/dashboards`).then((r) => r.dashboards),
  });
}

export function useDashboard(projectId: string | undefined, dashboardId: string | undefined) {
  return useQuery({
    queryKey: qk.dashboard(projectId ?? '', dashboardId ?? ''),
    enabled: Boolean(projectId && dashboardId),
    queryFn: () =>
      api
        .get<{ dashboard: Dashboard }>(`/projects/${projectId}/dashboards/${dashboardId}`)
        .then((r) => r.dashboard),
  });
}

export function useCreateDashboard(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; fromEndpointId?: string }) =>
      api.post<{ dashboard: Dashboard }>(`/projects/${projectId}/dashboards`, input).then((r) => r.dashboard),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.dashboards(projectId) }),
  });
}

export function useSaveDashboard(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      ...body
    }: {
      dashboardId: string;
      name?: string;
      description?: string;
      widgets?: Widget[];
      refreshMs?: number;
      minRole?: ProjectRole;
    }) =>
      api
        .patch<{ dashboard: Dashboard }>(`/projects/${projectId}/dashboards/${dashboardId}`, body)
        .then((r) => r.dashboard),
    onSuccess: (dashboard) => {
      qc.setQueryData(qk.dashboard(projectId, dashboard.id), dashboard);
      void qc.invalidateQueries({ queryKey: qk.dashboards(projectId) });
      // The overview embeds pinned dashboards, so any edit can change it.
      void qc.invalidateQueries({ queryKey: qk.overview(projectId) });
      // Widget data hangs off the dashboard key, so a definition change has to
      // drop it too — a collaborator gets this through entity:changed, but the
      // person who made the edit never receives their own broadcast.
      void qc.invalidateQueries({ queryKey: qk.dashboard(projectId, dashboard.id) });
    },
  });
}

/**
 * Pinning is a personal preference, not an edit: it needs only read access, so
 * a viewer can arrange their own overview.
 */
export function usePinDashboard(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, pinned }: { dashboardId: string; pinned: boolean }) =>
      api
        .put<{ dashboard: Dashboard }>(`/projects/${projectId}/dashboards/${dashboardId}/pin`, { pinned })
        .then((r) => r.dashboard),
    onSuccess: (dashboard) => {
      qc.setQueryData(qk.dashboard(projectId, dashboard.id), dashboard);
      void qc.invalidateQueries({ queryKey: qk.dashboards(projectId) });
      void qc.invalidateQueries({ queryKey: qk.overview(projectId) });
    },
  });
}

export function useDeleteDashboard(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dashboardId: string) => api.delete(`/projects/${projectId}/dashboards/${dashboardId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.dashboards(projectId) }),
  });
}

export function useRegenerateDashboard(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, endpointId }: { dashboardId: string; endpointId: string }) =>
      api
        .post<{ dashboard: Dashboard }>(`/projects/${projectId}/dashboards/${dashboardId}/regenerate`, {
          endpointId,
        })
        .then((r) => r.dashboard),
    onSuccess: (dashboard) => {
      qc.setQueryData(qk.dashboard(projectId, dashboard.id), dashboard);
    },
  });
}

export interface WidgetData {
  widgetId: string;
  raw: Json;
  rows: Record<string, Json>[];
  durationMs: number;
  fetchedAt: string;
  error?: string;
  /** Present only for widgets with server-side paging. */
  page?: number;
  pageSize?: number;
  total?: number;
}

export function useDashboardData(
  projectId: string | undefined,
  dashboardId: string | undefined,
  options?: {
    refreshMs?: number;
    variables?: Record<string, unknown>;
    /** Filter values scoped to one widget, keyed by widget id. */
    widgetVariables?: Record<string, Record<string, unknown>>;
    /** Current page per server-paginated widget, keyed by widget id. */
    pages?: Record<string, number>;
    enabled?: boolean;
  },
) {
  return useQuery({
    // Filters and paging are part of the identity of the result, so changing
    // either fetches rather than showing the previous page's rows.
    queryKey: [
      'project',
      projectId,
      'dashboard',
      dashboardId,
      'data',
      options?.variables ?? {},
      options?.widgetVariables ?? {},
      options?.pages ?? {},
    ],
    enabled: Boolean(projectId && dashboardId) && options?.enabled !== false,
    refetchInterval: options?.refreshMs && options.refreshMs > 0 ? options.refreshMs : false,
    staleTime: 0,
    // Keeps the current page on screen while the next one loads.
    placeholderData: (previous) => previous,
    queryFn: () =>
      api.post<{ widgets: WidgetData[]; fetchedAt: string }>(
        `/projects/${projectId}/dashboards/${dashboardId}/data`,
        {
          variables: options?.variables,
          widgetVariables: options?.widgetVariables,
          pages: options?.pages,
        },
      ),
  });
}

export function usePreviewWidget(projectId: string) {
  return useMutation({
    mutationFn: (widget: Widget) =>
      api.post<WidgetData>(`/projects/${projectId}/dashboards/preview`, { widget }),
  });
}

/* ------------------------------ datasources ------------------------------ */

export interface DatasourceType {
  kind: string;
  fields: { key: string; label: string; type: 'text' | 'number' | 'password' | 'boolean'; required?: boolean; placeholder?: string }[];
  secretKeys: string[];
}

export function useDatasourceTypes() {
  return useQuery({
    queryKey: qk.datasourceTypes,
    staleTime: Infinity,
    queryFn: () => api.get<{ types: DatasourceType[] }>('/datasource-types').then((r) => r.types),
  });
}

export function useDatasources(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.datasources(projectId ?? ''),
    enabled: Boolean(projectId) && enabled,
    queryFn: () =>
      api.get<{ datasources: Datasource[] }>(`/projects/${projectId}/datasources`).then((r) => r.datasources),
  });
}

export function useSaveDatasource(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      datasourceId,
      ...body
    }: {
      datasourceId?: string;
      name: string;
      kind?: string;
      config: Record<string, Json>;
    }) =>
      datasourceId
        ? api
            .patch<{ datasource: Datasource }>(`/projects/${projectId}/datasources/${datasourceId}`, {
              name: body.name,
              config: body.config,
            })
            .then((r) => r.datasource)
        : api
            .post<{ datasource: Datasource }>(`/projects/${projectId}/datasources`, body)
            .then((r) => r.datasource),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.datasources(projectId) }),
  });
}

export function useDeleteDatasource(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datasourceId: string) => api.delete(`/projects/${projectId}/datasources/${datasourceId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.datasources(projectId) }),
  });
}

export function useTestDatasource(projectId: string) {
  return useMutation({
    mutationFn: (datasourceId: string) =>
      api.post<{ ok: boolean; message: string }>(`/projects/${projectId}/datasources/${datasourceId}/test`),
  });
}

export function useDatasourceSchema(projectId: string | undefined, datasourceId: string | undefined | null) {
  return useQuery({
    queryKey: qk.datasourceSchema(projectId ?? '', datasourceId ?? ''),
    enabled: Boolean(projectId && datasourceId),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.get<{ tables: { name: string; columns: string[] }[]; error?: string }>(
        `/projects/${projectId}/datasources/${datasourceId}/schema`,
      ),
  });
}

/**
 * Live table structure for the schema designer: types, keys and links, plus
 * whether this connection may be reshaped at all.
 */
export interface DatasourceStructure {
  kind: string;
  tables: LiveTable[];
  editable: boolean;
  blocked?: string | null;
}

export function useDatasourceStructure(projectId: string | undefined, datasourceId: string | undefined) {
  return useQuery({
    queryKey: qk.datasourceStructure(projectId ?? '', datasourceId ?? ''),
    enabled: Boolean(projectId && datasourceId),
    queryFn: () =>
      api.get<DatasourceStructure>(`/projects/${projectId}/datasources/${datasourceId}/structure`),
  });
}

/** Compiles a plan to SQL without running it, for the review step. */
export function usePlanSchema(projectId: string, datasourceId: string) {
  return useMutation({
    mutationFn: (operations: SchemaOp[]) =>
      api.post<{ statements: string[] }>(
        `/projects/${projectId}/datasources/${datasourceId}/structure/plan`,
        { operations },
      ),
  });
}

export function useApplySchema(projectId: string, datasourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (operations: SchemaOp[]) =>
      api.post<{ applied: string[]; tables: LiveTable[] }>(
        `/projects/${projectId}/datasources/${datasourceId}/structure/apply`,
        { operations },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.datasourceStructure(projectId, datasourceId) });
      // The query editor's autocomplete reads the simpler listing.
      void qc.invalidateQueries({ queryKey: qk.datasourceSchema(projectId, datasourceId) });
    },
  });
}

/* -------------------------------- members -------------------------------- */

export function useMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: qk.members(projectId ?? ''),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ members: ProjectMember[] }>(`/projects/${projectId}/members`).then((r) => r.members),
  });
}

export function useAddMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: ProjectRole }) =>
      api.post<{ members: ProjectMember[] }>(`/projects/${projectId}/members`, input).then((r) => r.members),
    onSuccess: (members) => qc.setQueryData(qk.members(projectId), members),
  });
}

export function useUpdateMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: ProjectRole }) =>
      api
        .patch<{ members: ProjectMember[] }>(`/projects/${projectId}/members/${memberId}`, { role })
        .then((r) => r.members),
    onSuccess: (members) => qc.setQueryData(qk.members(projectId), members),
  });
}

export function useRemoveMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.delete(`/projects/${projectId}/members/${memberId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.members(projectId) }),
  });
}

/* --------------------------------- tokens -------------------------------- */

export function useTokens(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.tokens(projectId ?? ''),
    enabled: Boolean(projectId) && enabled,
    queryFn: () => api.get<{ tokens: ApiToken[] }>(`/projects/${projectId}/tokens`).then((r) => r.tokens),
  });
}

export interface CreatedTokenResponse {
  token: ApiToken;
  secret: string;
  usage: { baseUrl: string; header: string; example: string };
}

export function useCreateToken(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      scopes: TokenScope[];
      role: ProjectRole;
      expiresInDays: number | null;
    }) => api.post<CreatedTokenResponse>(`/projects/${projectId}/tokens`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.tokens(projectId) }),
  });
}

export function useRevokeToken(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => api.delete(`/projects/${projectId}/tokens/${tokenId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.tokens(projectId) }),
  });
}

/* ---------------------------------- logs --------------------------------- */

export interface LogFilters {
  endpointId?: string;
  status?: 'success' | 'error' | 'timeout';
  limit?: number;
}

export function useLogs(projectId: string | undefined, filters: LogFilters = {}) {
  const search = new URLSearchParams();
  if (filters.endpointId) search.set('endpointId', filters.endpointId);
  if (filters.status) search.set('status', filters.status);
  search.set('limit', String(filters.limit ?? 100));

  return useQuery({
    queryKey: qk.logs(projectId ?? '', filters),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ logs: RunLog[] }>(`/projects/${projectId}/logs?${search.toString()}`).then((r) => r.logs),
  });
}

export interface StatsResponse {
  since: string;
  stats: { total: number; success: number; error: number; timeout: number; avgDurationMs: number; p95DurationMs: number };
  series: { at: string; total: number; errors: number }[];
}

export function useStats(projectId: string | undefined, hours = 24, options?: Partial<UseQueryOptions<StatsResponse>>) {
  return useQuery<StatsResponse>({
    queryKey: qk.stats(projectId ?? '', hours),
    enabled: Boolean(projectId),
    queryFn: () => api.get<StatsResponse>(`/projects/${projectId}/stats?hours=${hours}`),
    ...options,
  });
}

/* ------------------------ instance administration ------------------------ */

/**
 * Everything below is superadmin-only on the server, so the hooks take an
 * `enabled` flag rather than guessing: a page that renders the tab for the
 * wrong person should not fire a request that comes back 403.
 */

export interface InstanceInfo {
  instance: {
    version: string;
    publicUrl: string;
    gatewayBase: string;
    uptimeSeconds: number;
    nodeVersion: string;
    environment: string;
  };
  settings: {
    allowRegistration: boolean;
    serveWeb: boolean;
    mcpEnabled: boolean;
    mcpPath: string;
    mcpEndpoint: string;
    mcpTools: number;
    mcpReadOnlyTools: number;
    flowTimeoutMs: number;
    flowMaxSteps: number;
    outboundAllowPrivateIps: boolean;
  };
  counts: Record<string, number>;
  realtime: { channels: number; messages: number };
}

export function useInstance(enabled = true) {
  return useQuery({
    queryKey: qk.instance,
    enabled,
    queryFn: () => api.get<InstanceInfo>('/admin/instance'),
  });
}

export interface AdminToken {
  id: string;
  name: string;
  prefix: string;
  userId: string;
  readOnly: boolean;
  createdBy: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  account: { id: string; email: string; name: string; disabled: boolean } | null;
}

export interface CreatedAdminTokenResponse {
  token: AdminToken;
  secret: string;
  usage: { mcpUrl: string; header: string; claudeCode: string; stdioBridge: string };
}

export function useAdminTokens(enabled = true) {
  return useQuery({
    queryKey: qk.adminTokens,
    enabled,
    queryFn: () => api.get<{ tokens: AdminToken[] }>('/admin/tokens').then((r) => r.tokens),
  });
}

export function useCreateAdminToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      email?: string;
      readOnly?: boolean;
      expiresInDays: number | null;
    }) => api.post<CreatedAdminTokenResponse>('/admin/tokens', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminTokens }),
  });
}

export function useRevokeAdminToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => api.delete(`/admin/tokens/${tokenId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminTokens }),
  });
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  globalRole: 'user' | 'superadmin';
  disabled: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  memberships: { projectId: string; role: ProjectRole }[];
}

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: qk.adminUsers(),
    enabled,
    queryFn: () => api.get<{ users: AdminUser[] }>('/admin/users').then((r) => r.users),
  });
}

export interface McpToolSummary {
  name: string;
  title: string;
  readOnly: boolean;
  destructive: boolean;
}

export interface McpCatalogue {
  total: number;
  readOnly: number;
  groups: { group: string; tools: McpToolSummary[] }[];
}

/** The tool list, from the server, so the page cannot list something stale. */
export function useMcpTools(enabled = true) {
  return useQuery({
    queryKey: qk.mcpTools,
    enabled,
    // The catalogue only changes when the server is redeployed.
    staleTime: 5 * 60 * 1000,
    queryFn: () => api.get<McpCatalogue>('/admin/mcp/tools'),
  });
}
